import { isEqual } from 'lodash-es';
import { useMemo, useRef, useState } from 'react';
import { useSearchParams as useSearchParamsBase } from 'react-router-dom';
import { useQueryContext } from '../http';
import { useHistoryStore } from '../store/history-store';
import { objSanitizeNilValues } from '../utils/object';
import { useMount } from './use-mount';

type QueryStringConfig<T> = {
  /** Names of parameters to watch (for selective updates) */
  watch?: Array<keyof T>;
  /** Debounce delay for rapid updates (e.g., typing) in milliseconds */
  debounce?: number;
};

export function useQueryStringState<
  T extends Record<string, string> & {
    page?: string | number;
    size?: string | number;
  } = Record<string, string>
>(config?: QueryStringConfig<T>) {
  const { previousParams, setPreviousParams } = useQueryContext();
  const add = useHistoryStore.useAdd();
  const [rawParams, _setParams] = useSearchParamsBase();

  // String-based key for stable memoization (avoids URLSearchParams ref instability)
  const paramsKey = rawParams.toString();
  const params = useMemo(
    () => (!rawParams ? {} : Object.fromEntries(rawParams)) as T,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsKey]
  );

  // Refs for timer cleanup and batching
  const pendingUpdatesRef = useRef<Partial<T>>({});
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Keep all mutable deps in a single ref, updated every render
  const depsRef = useRef({ _setParams, add, setPreviousParams, config });
  depsRef.current = { _setParams, add, setPreviousParams, config };

  // Clean up pending timers on unmount
  useMount(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  });

  // Stable actions created once -- identity never changes
  const [actions] = useState(() => {
    const getCurrentParams = (): T =>
      Object.fromEntries(new URLSearchParams(window.location.search)) as T;

    const getRelevantParams = (p: T): Partial<T> => {
      const watch = depsRef.current.config?.watch;
      if (!watch) return p;
      return watch.reduce((acc, key) => {
        if (p[key] !== undefined) {
          acc[key] = p[key];
        }
        return acc;
      }, {} as Partial<T>);
    };

    const applyUpdates = (
      updates: Partial<T>,
      skipHistory?: boolean,
      undoredo?: boolean
    ) => {
      const deps = depsRef.current;
      const currentParams = getCurrentParams();
      const newParams = objSanitizeNilValues(
        undoredo ? updates : { ...currentParams, ...updates }
      ) as T;

      if (isEqual(newParams, currentParams)) return;

      deps.setPreviousParams(currentParams);
      deps._setParams(new URLSearchParams(newParams));

      if (!skipHistory) {
        deps.add(getRelevantParams(newParams));
      }
    };

    const setParams = (
      values?: Partial<T>,
      skipHistory?: boolean,
      undoredo?: boolean
    ) => {
      if (!values) return;
      const debounceMs = depsRef.current.config?.debounce;
      if (!debounceMs) {
        applyUpdates(values, skipHistory, undoredo);
        return;
      }

      // Batch updates for debounced application
      pendingUpdatesRef.current = {
        ...pendingUpdatesRef.current,
        ...values,
      };

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        applyUpdates(pendingUpdatesRef.current, skipHistory, undoredo);
        pendingUpdatesRef.current = {};
      }, debounceMs);
    };

    const setParam = (name: keyof T, value?: string, skipHistory?: boolean) => {
      const currentParams = getCurrentParams();
      if (currentParams[name] !== value) {
        setParams({ [name]: value } as Partial<T>, skipHistory);
      }
    };

    const removeParam = (name: keyof T, skipHistory?: boolean) => {
      setParam(name, undefined, skipHistory);
    };

    const getParam = (name: string, defaultValue: string | null = null) => {
      const currentParams = getCurrentParams();
      return currentParams[name] ?? defaultValue;
    };

    const removeAllParams = () => {
      depsRef.current._setParams({});
    };

    const replaceParams = (values?: Partial<T>) => {
      const deps = depsRef.current;
      const currentParams = getCurrentParams();
      deps.setPreviousParams(currentParams);
      deps._setParams(new URLSearchParams(objSanitizeNilValues(values)));
    };

    return {
      setParams,
      setParam,
      removeParam,
      getParam,
      removeAllParams,
      replaceParams,
    };
  });

  // Only return relevant params if watching specific keys
  const returnedParams = useMemo(() => {
    if (!config?.watch) return params;
    return config.watch.reduce((acc, key) => {
      if (params[key] !== undefined) {
        acc[key] = params[key];
      }
      return acc;
    }, {} as Partial<T>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, config?.watch]);

  // actions is stable (never changes identity), so this only re-renders
  // when returnedParams or previousParams change
  return useMemo(
    () => ({
      params: returnedParams,
      ...actions,
      previousParams,
    }),
    [returnedParams, actions, previousParams]
  );
}
